import fs from "node:fs";
import path from "node:path";
import { downloadImage, getFilenameFromUrl, success, TaskState } from "@iflyrpa/share";
import FormData from "form-data";
import { AxiosProxyParams, errorResponse, MockPublish } from "../../types";
import { extractImgTag, replaceImgSrc } from "../../utils";
import { Http } from "../../utils/http";
import { reportLogger } from "../../utils/reportLogger";
import type { PublishAction } from ".";
import { generateTopicHtml } from "./utils";

interface CommonResponse<T = unknown> {
    data: T;
    errmsg: string;
    errno: number;
}

interface ImageRetResponse {
    bos_url: string;
    https_url: string;
    no_waterlog_bos_url: string;
    org_url: string;
}

const errnoMap: Record<number, string> = {
    20040706: "正文图片和封面图片推荐jpg、png格式。",
    20040084: "正文图片和封面图片推荐jpg、png格式。",
    20050004: "今日百家号发布次数已达上限，请明天再试。",
    10000015: "账号存在异常，需前往百家号平台完成发文验证后可激活使用。",
    20050000: "操作太频繁，请稍后再试",
    20040027: "你的账号状态异常，请到百家号平台检查账号状态。",
    10000017: "账号存在异常，需前往百家号平台完成发文验证后可激活使用。",
    20040033: "封面图片推荐 jpg、png格式，宽高在372×284-5000×5000 范围内。",
    2004005714: "网络环境异常，请检查网络后重试发布。",
    401100032: "异常，请检查图片（如：格式、版权、合规性等）。",
    20040034: "封面图片推荐jpg、png格式，不支持gif格式。",
    20040124: "服务器异常，请稍后重试！",
    20040001: "当前用户未登录，请登陆后重试！",
    401100025: "该应用不支持此媒资类型",
    401100033: "图片宽高不满足要求",
    20040105: "标题中不可使用表情！",
    20040051: "定时时间范围不符合要求!",
    "-6": "文章分类信息错误",
};

export const mockAction: PublishAction = async (task, params) => {
    const { baijiahaoSingleCover, baijiahaoMultCover, baijiahaoCoverType } = params.settingInfo;
    const tmpCachePath = task.getTmpPath();
    const headers = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        token: params.token,
    };

    const args: AxiosProxyParams = [
        { headers },
        task.logger,
        params.proxyLoc,
        params.accountId,
        "baijiahao", // 业务标识
    ];
    const http = new Http({ headers });
    const proxyHttp = new Http(...args);

    // 添加响应拦截器
    proxyHttp.addResponseInterceptor<CommonResponse>((response) => {
        const msgType = params.saveType === "draft" ? "同步" : "发布";

        // 检查响应中的 errno 字段
        if (response && response.data.errno !== 0) {
            let errmsg = response.config.defaultErrorMsg || `文章${msgType}异常，请稍后重试。`;

            if (response.data.errno === 20050000) {
                errmsg = msgType + errnoMap[response.data.errno];
            }
            if (response.data.errno === 401100032) {
                errmsg = `图片${msgType}${errnoMap[response.data.errno]}`;
            } else if (errnoMap[response.data.errno]) {
                errmsg = errnoMap[response.data.errno];
            } else if (response.data.errmsg) {
                errmsg = response.data.errmsg;
            }

            return {
                code: errnoMap[response.data.errno] ? 414 : 500,
                message: errmsg,
                data: response.data,
            };
        }
    });

    // 0. 校验登录
    await http.api({
        method: "get",
        url: "https://baijiahao.baidu.com/pcui/home/index",
        defaultErrorMsg: `百家号登录状态失效，请重新绑定账号后${params.saveType === "draft" ? "同步" : "发布"}。`,
    });

    // 1. 获取用户 appId
    const appIdInfo = await http.api<CommonResponse<{ user?: { app_id: number } }>>({
        method: "post",
        url: "https://baijiahao.baidu.com/builder/app/appinfo",
        defaultErrorMsg: "创建草稿失败，请稍后重试。",
    });

    const appId = appIdInfo.data?.user?.app_id;

    if (!appId) return { code: 414, message: "创建草稿失败，请稍后重试。", data: "" };

    const uploadImages = async (_images: string[]) => {
        // 过滤掉空字符串
        const images = _images.filter((url) => !!url && url.trim() !== "");

        // 下载图片到本地
        return await Promise.all(
            images.map(async (url) => {
                const fileName = getFilenameFromUrl(url);
                const ext = path.extname(fileName).toLowerCase();

                // 检查图片格式（百家号仅支持 jpg、png）
                if (![".jpg", ".jpeg", ".png"].includes(ext)) {
                    throw {
                        code: 414,
                        message: "图片格式不支持，百家号仅支持 jpg、png 格式，请转换后重试。",
                        data: "",
                    };
                }

                const image = await downloadImage(url, path.join(tmpCachePath, fileName));

                // 检查文件大小（百家号限制 5MB）
                const stats = fs.statSync(image);
                const maxSize = 5 * 1024 * 1024; // 5MB
                if (stats.size > maxSize) {
                    throw {
                        code: 414,
                        message: "百家号平台：单张图片不得超过 5MB",
                        data: "",
                    };
                }

                const formData = new FormData();
                formData.append("org_file_name", fileName);
                formData.append("type", "image");
                formData.append("app_id", appId);
                formData.append("is_waterlog", "1");
                formData.append("save_material", "1");
                formData.append("no_compress", "0");
                formData.append("article_type", "news");
                formData.append("media", fs.createReadStream(image));

                try {
                    const res = await http.api<{ ret: ImageRetResponse }>(
                        {
                            method: "post",
                            url: "https://baijiahao.baidu.com/materialui/picture/uploadProxy",
                            data: formData,
                            defaultErrorMsg:
                                params.saveType === "draft"
                                    ? "图片上传异常，请稍后重试同步。"
                                    : "图片上传异常，请稍后重试发布。",
                        },
                        { retries: 3, retryDelay: 20, timeout: 2 * 60 * 1000 }, // 增加到2分钟，适应大图片上传
                    );

                    return res.ret;
                } catch (error) {
                    console.error("图片上传失败，详细错误信息：");
                    console.error("错误对象：", error);
                    console.error("错误堆栈：", error instanceof Error ? error.stack : "无堆栈信息");
                    console.error("图片URL：", url);
                    console.error("文件名：", fileName);
                    throw error;
                }
            }),
        );
    };

    // 2. 提取并上传正文中的图片
    const originContentImages = extractImgTag(params.content);
    const targetContentImages = await uploadImages(originContentImages);

    if (params.settingInfo.baijiahaoTopic && !params.settingInfo.baijiahaoTopic?.id) {
        const topicCheck = await http.api<{ errno: number; errmsg: string }>({
            method: "get",
            url: "https://baijiahao.baidu.com/pcui/topic/topiccheck",
            params: {
                topicName: params.settingInfo.baijiahaoTopic?.title,
            },
        });
        if (topicCheck.errno !== 0) {
            return { code: 414, message: topicCheck.errmsg, data: "" };
        }
    }

    // 替换正文中的图片
    const content = replaceImgSrc(
        params.settingInfo?.baijiahaoTopic?.title
            ? params.content +
                  generateTopicHtml(
                      params.settingInfo?.baijiahaoTopic?.title,
                      params.settingInfo?.baijiahaoTopic?.id || "",
                      params.settingInfo.baijiahaoTopic?.sv_small_images || "",
                  )
            : params.content,
        (dom) => {
            if (dom.attribs.src) {
                const idx = originContentImages.indexOf(dom.attribs.src.replace(/&amp;/g, "&"));
                dom.attribs.src = targetContentImages[idx].bos_url;
                dom.attribs["data-bjh-origin-src"] = targetContentImages[idx].org_url;
                dom.attribs["data-bjh-params"] = targetContentImages[idx].org_url;
                dom.attribs["data-bjh-type"] = "IMG";
            }

            return dom;
        },
    );

    // 3. 上传竖版封面
    // let vertical_cover = params.settingInfo.baijiahaoVerticalCover;
    // if (vertical_cover) {
    // 	vertical_cover = (await uploadImages([vertical_cover]))[0].bos_url;
    // }

    // 4. 上传横版封面
    const horizontalCoverUrl = baijiahaoCoverType === "single" ? [baijiahaoSingleCover] : baijiahaoMultCover;
    const horizontalCoverRes = await uploadImages(horizontalCoverUrl);
    const cover_images = horizontalCoverRes.map((item) => ({
        src: item.bos_url,
        machine_chooseimg: 0,
        isLegal: 0,
    }));
    const _cover_images_map = horizontalCoverRes.map((item) => ({
        src: item?.bos_url,
        origin_src: item?.org_url,
    }));

    // 5. 构造发布数据
    const isAi = params.settingInfo.baijiahaoIsAi.includes("isAi");
    const activityList = params.settingInfo.baijiahaoSelectActivityCache;
    const cover_layout = baijiahaoCoverType === "single" ? "one" : "three";

    const activity_list = [
        ...(activityList.length > 0 ? [{ id: `task_${activityList[0]}`, is_checked: 1 }] : []),
        ...(params.settingInfo.baijiahaoPublishConf?.map((item) => ({
            id: item.confName,
            is_checked: item.isChecked ? 1 : 0,
        })) || []),
    ];

    if (isAi && !activity_list.some((item) => "aigc_bjh_status" in item)) {
        activity_list.push({ id: "aigc_bjh_status", is_checked: 1 });
    } // 兼容旧版配置

    const publishData: Record<string, unknown> = {
        title: params.settingInfo.baijiahaoTitle,
        content,
        // vertical_cover,
        abstract: params.settingInfo.baijiahaoAbstract,
        cover_layout,
        cover_images: JSON.stringify(cover_images),
        _cover_images_map: JSON.stringify(_cover_images_map),
        type: "news",
        source_reprinted_allow: 0,
        abstract_from: 1,
        isBeautify: "false",
        usingImgFilter: "false",
        source: "upload",
        cover_source: "upload",
        event2news: params.settingInfo?.baijiahaoEvent2News ? 2 : 0,
        activity_list,
        ...(params.settingInfo.timer ? { timer_time: params.settingInfo.timer } : {}),
        ...(params.settingInfo.baijiahaoCms
            ? {
                  "cate_user_cms[0]": params.settingInfo.baijiahaoCms[0],
                  "cate_user_cms[1]": params.settingInfo.baijiahaoCms[1],
              }
            : {}),
        ...(params.settingInfo.baijiahaoEventSpec
            ? {
                  "event_spec[time]": params.settingInfo.baijiahaoEventSpec.time,
                  "event_spec[pos]": params.settingInfo.baijiahaoEventSpec.pos,
              }
            : {}),
    };

    const isDraft = params.saveType === "draft";

    const saveUrl = isDraft
        ? "https://baijiahao.baidu.com/pcui/article/save?callback=bjhdraft"
        : "https://baijiahao.baidu.com/pcui/article/publish?callback=bjhpublish";

    task._timerRecord.PrePublish = Date.now();

    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    if (MockPublish) {
        const message = `文章模拟发布成功 ${http.proxyInfo}`;
        const data = "123456789";

        await updateTaskState?.({
            state: TaskState.SUCCESS,
            result: {
                response: data,
            },
        });
        return success(data, message);
    }

    const res = await proxyHttp.api<{
        errno: number;
        ret: { article_id: string };
        proxyInfo: Record<string, unknown>;
        errmsg: string;
    }>(
        {
            method: "post",
            url: saveUrl,
            data: publishData,
            headers: {
                "content-type": "application/x-www-form-urlencoded",
                origin: "https://baijiahao.baidu.com",
                referer: "https://baijiahao.baidu.com/builder/rc/edit?type=news",
            },
            defaultErrorMsg: isDraft ? "文章同步出现异常，请稍后重试。" : "文章发布出现异常，请稍后重试。",
        },
        { retries: 3, retryDelay: 500, timeout: 20 * 1000 },
    );

    if (!isDraft) {
        reportLogger({
            token: params.huiwenToken || "",
            enverionment: task.enverionment || "development",
            postId: params.articleId,
            eip: proxyHttp.proxyInfo,
            accountId: params.accountId,
            uid: params.uid,
            platform: "baijiahao",
            publishParams: publishData,
        });
    }

    task.logger.info(`[baijiahaoPublish] publishResult: ${JSON.stringify(res)} `);

    const isSuccess = res.errno === 0;
    const data = res?.ret?.article_id;
    const message = isSuccess
        ? isDraft
            ? "文章同步成功！"
            : `文章发布成功！${res.proxyInfo || ""}`
        : res.errmsg || (isDraft ? "文章同步失败，请稍后重试。" : "文章发布失败，请稍后重试。");

    if (!isSuccess) {
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: message,
        });
        return { code: 414, data, message };
    }

    await updateTaskState?.({
        state: TaskState.SUCCESS,
        result: {
            response: data,
        },
    });
    // 临时返回结束

    return success(
        res.errno === 0 ? res?.ret?.article_id || "" : "",
        res.errno === 0 ? `文章发布成功！${http.proxyInfo || ""}` : (res.errmsg ?? "文章发布失败，请稍后重试。"),
    );
};
